<template>
  <div>
    <div class="px-5 mt-3">
      <v-row dense v-if="finalizacao">
        <v-col cols="12" sm="6">
          <div class="tittle font-weight-bold">
            Nova solicitação de compra
          </div>
          <div class="body-2">
            Número do pedido Proxpect - {{ finalizacao.id }}
          </div>
          <div class="body-2" v-if="showNumeroOrdemCompra">
            Número da ordem de compra - {{ finalizacao.codigoInterno }}
          </div>
        </v-col>
        <v-col cols="12" sm="6" class="fornecedor-nome text-right body-2">
          <v-tooltip bottom>
            <template v-slot:activator="{ on }">
              <span v-on="on">
                {{ finalizacao.fornecedor.pessoa.nome }}
                ( <px-fornecedor-documento :fornecedor="finalizacao.fornecedor"/>)
              </span>
            </template>
            <span>Empresa fornecedora</span>
          </v-tooltip>
        </v-col>
      </v-row>
    </div>
    <div v-if="loading" class="text-center">
      <px-circular-loading/>
    </div>
    <div v-if="!loading && finalizacao" class="mt-3">
      <px-finalizacao-dados-basicos :compra-id="cotacaoId"
                                    :show-print-pdf="showPrintPdf"
                                    :finalizacao="finalizacao"
                                    @movimentar="onMovimentar"/>
      <px-finalizacao-tabs :finalizacao="finalizacao"/>
      <px-finalizacao-mensagens-comprador v-if="checkMessages"
                                          :finalizacao="finalizacao"/>
      <px-finalizacao-produtos :finalizacao="finalizacao" :compra-id="cotacaoId"/>
      <px-finalizacao-saldos-entregas :compra-id="cotacaoId"/>
      <px-aviso-plataforma-fornecedor-simple class="mt-5" v-if="!loading && finalizacao"/>
    </div>
    <px-auth-dialog v-model="showAuth"
                    :cotacao-id="cotacaoId"
                    :is-finalizacao="true"
                    @validate="onValidate"/>
    <px-finalizacao-confirm v-model="showConfirm"
                            v-if="finalizacao"
                            :request-id="cotacaoId"
                            :compra-id="finalizacao.id"
                            :name="finalizacao.comprador.pessoa.nome"
                            @update="onUpdateConfirm"/>

    <!--    <px-aviso-plataforma-fornecedor v-if="!loading && finalizacao"/>-->
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ErpCamposIntegracao, SituacaoCompra } from 'px-business-components';
import AuthDialog from '@/modules/cotacao/AuthDialog.vue';
import AuthService from '@/modules/cotacao/auth-service';
import eventsMixin from '@/mixins/events-mixin';
import FinalizacaoConfirm from '@/modules/finalizacao/FinalizacaoConfirm.vue';
import FinalizacaoSaldosEntregas from '@/modules/finalizacao/FinalizacaoSaldosEntregas.vue';
import { isNaN } from 'lodash';
import AcessosService from '@/api/acessos-service';
import FinalizacaoService from './finalizacao-service';
import FinalizacaoDadosBasicos from './FinalizacaoDadosBasicos.vue';
import FinalizacaoProdutos from './FinalizacaoProdutos.vue';
import FinalizacaoTabs from './FinalizacaoTabs.vue';
import FinalizacaoMensagensComprador from './FinalizacaoMensagensComprador.vue';

const recaptchaKey = process.env.VUE_APP_RECAPTCHA_KEY;
const REGISTRO_ACESSO_THRESHOLD_MINUTOS = 60;
const REGISTRO_ACESSO_KEY_PREFIX = 'acesso_registrado_compra_';

export default {
  mixins: [eventsMixin],
  data() {
    return {
      showAuth: false,
      showConfirm: false,
      loading: false,
      finalizacao: null,
      receiver: this.$route.query.receiver,
    };
  },
  computed: {
    ...mapGetters({
      cotacaoId: 'cotacaoId',
      isAuthenticated: 'isAuthenticatedFinalizacao',
      tokenFinalizacao: 'tokenFinalizacao',
    }),
    isPendenteFornecedor() {
      return this.finalizacao.situacao === SituacaoCompra.keys.PENDENTE_FORNECEDOR.key;
    },
    checkMessages() {
      return this.finalizacao.mensagensPersonalizadas
        && this.finalizacao.mensagensPersonalizadas.length;
    },
    showNumeroOrdemCompra() {
      if (!this.finalizacao) {
        return false;
      }
      if (this.finalizacao.entidadeComprador.erpCamposIntegracao
        === ErpCamposIntegracao.keys.NEO_G7.key) {
        return false;
      }
      return !!this.finalizacao.codigoInterno;
    },
    showPrintPdf() {
      return this.finalizacao
        && this.finalizacao.entidadeComprador.permiteImpressaoRelatorioFornecedor;
    },
  },
  methods: {
    verificarEnvioRegistroAcesso(key) {
      const ultimoRegistro = sessionStorage.getItem(key);

      if (!ultimoRegistro) {
        return true;
      }

      const dhUltimoRegistro = new Date(ultimoRegistro);
      if (isNaN(dhUltimoRegistro.getTime())) {
        sessionStorage.removeItem(key);
        return true;
      }

      const diffMinutos = (new Date() - dhUltimoRegistro) / 1000 / 60;
      return diffMinutos >= REGISTRO_ACESSO_THRESHOLD_MINUTOS;
    },
    registraAcesso() {
      if (!this.receiver) {
        return Promise.resolve();
      }

      const key = `${REGISTRO_ACESSO_KEY_PREFIX}${this.cotacaoId}`;
      const enviarRegistro = this.verificarEnvioRegistroAcesso(key);

      if (!enviarRegistro) {
        return Promise.resolve();
      }

      return AcessosService.registraAcessoCompra(this.cotacaoId, {
        contato: this.receiver,
      })
        .then(() => {
          sessionStorage.setItem(key, new Date().toISOString());
        });
    },
    async validate() {
      this.loading = true;
      try {
        const recaptchaToken = recaptchaKey ? await this.recaptcha() : undefined;
        await AuthService.validarFinalizacao(this.cotacaoId,
          { token: this.tokenFinalizacao, recaptcha: recaptchaToken });
        await this.getFinalizacao();
      } catch (err) {
        this.showAuth = true;
      } finally {
        this.loading = false;
      }
    },
    onValidate() {
      this.loading = true;
      this.getFinalizacao()
        .finally(() => {
          this.loading = false;
        });
    },
    onMovimentar() {
      this.getFinalizacao();
    },
    getFinalizacao() {
      return FinalizacaoService.getFinalizacao(this.cotacaoId)
        .then((response) => {
          this.finalizacao = response.data;
          this.launchConfirmDialog();
          this.createEventAccess(this.eventsResource.COMPRA,
            { solicitacao: this.cotacaoId }, true);
          if (this.isPendenteFornecedor) {
            window.addEventListener('beforeunload', this.beforeWindowUnload);
          }
          return this.registraAcesso();
        });
    },
    launchConfirmDialog() {
      if (this.finalizacao.status === 'COMPRA_RECEBIDA'
        || this.finalizacao.situacao !== SituacaoCompra.keys.APROVADA.key) {
        return;
      }
      this.showConfirm = true;
    },
    onUpdateConfirm() {
      this.finalizacao.status = 'COMPRA_RECEBIDA';
      this.showConfirm = false;
    },
    beforeWindowUnload(e) {
      e.preventDefault();
      e.returnValue = '';
    },
    async recaptcha() {
      await this.$recaptchaLoaded();
      return this.$recaptcha();
    },
  },
  created() {
    if (!this.isAuthenticated) {
      this.showAuth = true;
      return;
    }
    this.validate();
  },
  components: {
    pxAuthDialog: AuthDialog,
    pxFinalizacaoDadosBasicos: FinalizacaoDadosBasicos,
    pxFinalizacaoProdutos: FinalizacaoProdutos,
    pxFinalizacaoConfirm: FinalizacaoConfirm,
    pxFinalizacaoTabs: FinalizacaoTabs,
    pxFinalizacaoMensagensComprador: FinalizacaoMensagensComprador,
    pxFinalizacaoSaldosEntregas: FinalizacaoSaldosEntregas,
  },
};
</script>
<style scoped>
.tittle {
  font-size: 30px;
}

.fornecedor-nome {
  margin-top: 15px;
}
</style>
